/**
 * TaxClassificationSection — lets the user classify bank transactions in the
 * tax-payment Plaid category, unclassify existing rows, and declare
 * standalone estimated payments (federal/state) for a given tax year.
 *
 * Kept self-contained: owns its form state + tRPC mutations so it can be
 * dropped into the tax dashboard (or anywhere else) without leaking details.
 */
import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import {
  TAX_TRANSACTION_TYPES,
  TAX_JURISDICTIONS,
  type TaxTransactionRow,
  type TaxTransactionType,
  type TaxJurisdiction,
  type UncategorizedTaxTransaction,
} from '@perdiem/trpc/tax-transactions';
import { trpc } from '../lib/trpc';
import { webPrivacyProps } from '../lib/privacy';
import { formatCurrency } from '../lib/utils';
import { Button } from './Button';
import { Select, type SelectOption } from './Select';
import { TextInput } from './TextInput';
import { useConfirmationDialog } from './ConfirmationDialog';

const TYPE_LABELS: Record<TaxTransactionType, string> = {
  estimated_payment: 'Estimated payment',
  balance_due: 'Balance due (prior year)',
  refund: 'Refund (prior year)',
  withholding: 'Withholding',
  property_tax: 'Property tax',
  other: 'Not tax-related',
};

const JURISDICTION_LABELS: Record<TaxJurisdiction, string> = {
  federal: 'Federal',
  state: 'State',
  local: 'Local',
};

const TYPE_OPTIONS: SelectOption<TaxTransactionType>[] = TAX_TRANSACTION_TYPES.map(
  (v) => ({ value: v, label: TYPE_LABELS[v] }),
);
const JURISDICTION_OPTIONS: SelectOption<TaxJurisdiction>[] =
  TAX_JURISDICTIONS.map((v) => ({ value: v, label: JURISDICTION_LABELS[v] }));

function formatTaxCurrency(value: number): string {
  return formatCurrency(value, { decimals: 0 });
}

function buildYearOptions(current: number): SelectOption<string>[] {
  // Estimated payments can apply to current year or prior year (Q4 is payable
  // by Jan 15 of the following year). Nothing else needs a year picker.
  return [current - 1, current].map((y) => ({
    value: String(y),
    label: String(y),
  }));
}

function ClassifyRow({
  tx,
  currentYear,
}: {
  tx: UncategorizedTaxTransaction;
  currentYear: number;
}) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState<TaxTransactionType>('estimated_payment');
  const [jurisdiction, setJurisdiction] = useState<TaxJurisdiction>('federal');
  const [taxYear, setTaxYear] = useState<string>(String(currentYear));

  const classify = trpc.tax.classifyTransaction.useMutation({
    onSuccess: () => {
      void utils.tax.currentYearEstimate.invalidate();
      void utils.budget.invalidate();
      setEditing(false);
    },
  });

  if (!editing) {
    return (
      <Pressable
        onPress={() => setEditing(true)}
        className="py-3 border-b border-gray-100 dark:border-gray-700 active:opacity-70"
      >
        <View className="flex-row items-center justify-between">
          <View className="flex-1 mr-3">
            <Text
              className="text-sm font-medium text-gray-800 dark:text-gray-200"
              numberOfLines={1}
              {...webPrivacyProps('private')}
            >
              {tx.name}
            </Text>
            <View className="flex-row items-center gap-2 mt-0.5">
              <Text className="text-xs text-gray-500 dark:text-gray-400">{tx.date}</Text>
              <Text className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                · Tap to classify
              </Text>
            </View>
          </View>
          <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200" {...webPrivacyProps('financial')}>
            {formatTaxCurrency(tx.amount)}
          </Text>
        </View>
      </Pressable>
    );
  }

  return (
    <View className="py-3 border-b border-gray-100 dark:border-gray-700">
      <View className="flex-row items-center justify-between mb-2">
        <View className="flex-1">
          <Text
            className="text-sm font-medium text-gray-800 dark:text-gray-200"
            numberOfLines={1}
            {...webPrivacyProps('private')}
          >
            {tx.name}
          </Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400">{tx.date}</Text>
        </View>
        <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200 ml-3" {...webPrivacyProps('financial')}>
          {formatTaxCurrency(tx.amount)}
        </Text>
      </View>
      <View className="flex-row gap-2 flex-wrap">
        <View className="flex-1 min-w-[140px]">
          <Select<TaxTransactionType>
            value={type}
            options={TYPE_OPTIONS}
            onChange={setType}
            size="sm"
          />
        </View>
        {type !== 'property_tax' && type !== 'other' && (
          <View className="flex-1 min-w-[100px]">
            <Select<TaxJurisdiction>
              value={jurisdiction}
              options={JURISDICTION_OPTIONS}
              onChange={setJurisdiction}
              size="sm"
            />
          </View>
        )}
        {type === 'estimated_payment' && (
          <View className="w-20">
            <Select<string>
              value={taxYear}
              options={buildYearOptions(currentYear)}
              onChange={setTaxYear}
              size="sm"
            />
          </View>
        )}
        <Button
          size="sm"
          loading={classify.isPending}
          onPress={() =>
            classify.mutate({
              transactionId: tx.transactionId,
              type,
              jurisdiction,
              taxYear:
                type === 'estimated_payment'
                  ? parseInt(taxYear, 10)
                  : type === 'refund' || type === 'balance_due'
                    ? currentYear - 1
                    : currentYear,
            })
          }
        >
          Classify
        </Button>
        <Button variant="ghost" size="sm" onPress={() => setEditing(false)}>
          Cancel
        </Button>
      </View>
    </View>
  );
}

function ClassifiedRow({
  row,
  currentYear,
}: {
  row: TaxTransactionRow;
  currentYear: number;
}) {
  const utils = trpc.useUtils();
  const { confirmDestructive } = useConfirmationDialog();
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState<TaxTransactionType>(row.type);
  const [jurisdiction, setJurisdiction] = useState<TaxJurisdiction>(row.jurisdiction);
  const [taxYear, setTaxYear] = useState<string>(String(row.taxYear));

  const invalidate = () => {
    void utils.tax.currentYearEstimate.invalidate();
    void utils.budget.invalidate();
  };
  const classify = trpc.tax.classifyTransaction.useMutation({
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });
  const unclassify = trpc.tax.unclassify.useMutation({
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });

  const isRefund = row.type === 'refund';
  const label = `${JURISDICTION_LABELS[row.jurisdiction]} · ${TYPE_LABELS[row.type]}`;
  const sub = row.linkedTransaction
    ? `${row.linkedTransaction.date} — ${row.linkedTransaction.name}`
    : row.note || 'Declared payment';

  const startEditing = () => {
    setType(row.type);
    setJurisdiction(row.jurisdiction);
    setTaxYear(String(row.taxYear));
    setEditing(true);
  };

  if (!editing) {
    return (
      <Pressable
        onPress={startEditing}
        className="flex-row items-center py-2 border-b border-gray-100 dark:border-gray-700 active:opacity-70"
      >
        <View className="flex-1">
          <Text className="text-sm text-gray-800 dark:text-gray-200">{label}</Text>
          <Text
            className="text-xs text-gray-500 dark:text-gray-400"
            numberOfLines={1}
            {...webPrivacyProps('private')}
          >
            {sub} · Tax year {row.taxYear}
          </Text>
        </View>
        <Text
          className={`text-sm font-semibold ${
            isRefund
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-800 dark:text-gray-200'
          }`}
          {...webPrivacyProps('financial')}
        >
          {isRefund ? '− ' : ''}
          {formatTaxCurrency(Math.abs(row.amount))}
        </Text>
      </Pressable>
    );
  }

  return (
    <View className="py-3 border-b border-gray-100 dark:border-gray-700">
      <View className="flex-row items-center justify-between mb-2">
        <View className="flex-1">
          <Text
            className="text-sm font-medium text-gray-800 dark:text-gray-200"
            numberOfLines={1}
            {...webPrivacyProps('private')}
          >
            {row.linkedTransaction?.name ?? 'Declared payment'}
          </Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            {row.linkedTransaction?.date ?? ''}
          </Text>
        </View>
        <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200 ml-3" {...webPrivacyProps('financial')}>
          {formatTaxCurrency(Math.abs(row.amount))}
        </Text>
      </View>
      <View className="flex-row gap-2 flex-wrap">
        <View className="flex-1 min-w-[140px]">
          <Select<TaxTransactionType>
            value={type}
            options={TYPE_OPTIONS}
            onChange={setType}
            size="sm"
          />
        </View>
        {type !== 'property_tax' && type !== 'other' && (
          <View className="flex-1 min-w-[100px]">
            <Select<TaxJurisdiction>
              value={jurisdiction}
              options={JURISDICTION_OPTIONS}
              onChange={setJurisdiction}
              size="sm"
            />
          </View>
        )}
        {type === 'estimated_payment' && (
          <View className="w-20">
            <Select<string>
              value={taxYear}
              options={buildYearOptions(currentYear)}
              onChange={setTaxYear}
              size="sm"
            />
          </View>
        )}
        <Button
          size="sm"
          loading={classify.isPending}
          onPress={() => {
            if (!row.linkedTransaction) return;
            if (row.transactionId == null) return;
            classify.mutate({
              transactionId: row.transactionId,
              type,
              jurisdiction,
              taxYear:
                type === 'estimated_payment'
                  ? parseInt(taxYear, 10)
                  : type === 'refund' || type === 'balance_due'
                    ? currentYear - 1
                    : currentYear,
            });
          }}
        >
          Save
        </Button>
        <Button variant="ghost" size="sm" onPress={() => setEditing(false)}>
          Cancel
        </Button>
        {row.transactionId != null && (
          <Button
            variant="ghost"
            size="sm"
            loading={unclassify.isPending}
            onPress={async () => {
              const confirmed = await confirmDestructive({
                title: 'Remove classification?',
                message: 'This keeps the bank transaction but removes the tax link.',
              });
              if (!confirmed) return;
              unclassify.mutate({ id: row.id });
            }}
          >
            Unclassify
          </Button>
        )}
      </View>
    </View>
  );
}

function EstimatedPaymentInput({
  label,
  jurisdiction,
  currentYear,
  initialAmount,
}: {
  label: string;
  jurisdiction: TaxJurisdiction;
  currentYear: number;
  initialAmount: number;
}) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(
    initialAmount > 0 ? initialAmount.toFixed(2) : '',
  );
  const setMutation = trpc.tax.setDeclaredEstimated.useMutation({
    onSuccess: () => {
      void utils.tax.currentYearEstimate.invalidate();
      setEditing(false);
    },
  });

  const startEditing = () => {
    setValue(initialAmount > 0 ? initialAmount.toFixed(2) : '');
    setEditing(true);
  };
  const cancel = () => {
    setValue(initialAmount > 0 ? initialAmount.toFixed(2) : '');
    setEditing(false);
  };
  const save = () => {
    const parsed = parseFloat(value);
    const amount = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    if (Math.abs(amount - initialAmount) < 0.005) {
      setEditing(false);
      return;
    }
    setMutation.mutate({ jurisdiction, taxYear: currentYear, amount });
  };

  if (!editing) {
    return (
      <Pressable
        onPress={startEditing}
        className="flex-row items-center justify-between py-2.5 active:opacity-70"
      >
        <Text className="text-sm text-gray-700 dark:text-gray-300">{label}</Text>
        <Text
          className={`text-sm ${
            initialAmount > 0
              ? 'text-gray-800 dark:text-gray-200 font-medium'
              : 'text-gray-400 dark:text-gray-500'
          }`}
          {...webPrivacyProps('financial')}
        >
          {initialAmount > 0 ? formatTaxCurrency(initialAmount) : 'Add'}
        </Text>
      </Pressable>
    );
  }

  return (
    <View className="flex-row items-center gap-2 py-2">
      <Text className="w-20 text-sm text-gray-700 dark:text-gray-300">{label}</Text>
      <View className="flex-1">
        <TextInput
          placeholder="0.00"
          value={value}
          onChangeText={setValue}
          keyboardType="decimal-pad"
          size="sm"
          autoFocus
        />
      </View>
      <Button size="sm" loading={setMutation.isPending} onPress={save}>
        Save
      </Button>
      <Button variant="ghost" size="sm" onPress={cancel}>
        Cancel
      </Button>
    </View>
  );
}

function EstimatedPaymentsForm({
  currentYear,
  classified,
}: {
  currentYear: number;
  classified: TaxTransactionRow[];
}) {
  const declaredByJurisdiction = (j: TaxJurisdiction) =>
    classified
      .filter(
        (r) =>
          r.type === 'estimated_payment' &&
          r.jurisdiction === j &&
          r.transactionId === null,
      )
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);

  return (
    <View>
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        Estimated payments you&apos;ve mailed or scheduled for {currentYear}.
        Enter the total for the year — payments from linked bank accounts are
        added automatically.
      </Text>
      <EstimatedPaymentInput
        label="Federal"
        jurisdiction="federal"
        currentYear={currentYear}
        initialAmount={declaredByJurisdiction('federal')}
      />
      <EstimatedPaymentInput
        label="State"
        jurisdiction="state"
        currentYear={currentYear}
        initialAmount={declaredByJurisdiction('state')}
      />
    </View>
  );
}

interface Props {
  currentYear: number;
  uncategorized: UncategorizedTaxTransaction[];
  classified: TaxTransactionRow[];
}

export function TaxClassificationSection({
  currentYear,
  uncategorized,
  classified,
}: Props) {
  // Declared estimated payments are managed exclusively by the form below —
  // don't also list them as classified rows. Also hide "other" since it
  // doesn't contribute to any tax projection.
  const classifiedRows = classified.filter(
    (r) =>
      r.type !== 'other' &&
      !(r.type === 'estimated_payment' && r.transactionId === null),
  );

  return (
    <View className="gap-4">
      {uncategorized.length > 0 && (
        <View>
          {uncategorized.map((tx) => (
            <ClassifyRow key={tx.transactionId} tx={tx} currentYear={currentYear} />
          ))}
        </View>
      )}

      {classifiedRows.length > 0 && (
        <View>
          {classifiedRows.map((row) => (
            <ClassifiedRow key={row.id} row={row} currentYear={currentYear} />
          ))}
        </View>
      )}

      <View
        className={
          uncategorized.length + classifiedRows.length > 0
            ? 'pt-2 border-t border-gray-100 dark:border-gray-700'
            : ''
        }
      >
        <EstimatedPaymentsForm currentYear={currentYear} classified={classified} />
      </View>
    </View>
  );
}
